export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const PASSWORD_RULES = {
  MIN_LENGTH: 8,
  LOWERCASE: /[a-z]/,
  UPPERCASE: /[A-Z]/,
  NUMBER: /\d/,
  SPECIAL: /[!@#$%^&*()]/,
} as const

// A display name, not an identifier. The upper bound is generous on purpose —
// patronymics, multiple surnames and honorifics run long, and a signup form is
// the worst place to tell someone their real name is too big — while staying
// well inside the varchar(255) the IAM `users.name` column gives us, with room
// to spare for the multi-byte scripts those names are often written in.
export const NAME_RULES = {
  MIN_LENGTH: 2,
  MAX_LENGTH: 100,
} as const

// SecureID requires an organization name on signup and bounds it 2-100, the
// same window as the person's name. Kept as its own constant rather than reusing
// NAME_RULES because they are two different things that happen to agree today —
// one is a display name, the other is what the tenant will be called forever.
export const ORG_RULES = {
  MIN_LENGTH: 2,
  MAX_LENGTH: 100,
} as const

export const PASSWORD_MAX_SCORE = 6
export const PASSWORD_SECURE_SCORE = 6

export const OTP_LENGTH = 6
export const OTP_RESEND_COOLDOWN_SECONDS = 60

export const AUTH_STORAGE_KEYS = {
  SIGNUP_EMAIL: 'signup_email',
  RESET_EMAIL: 'reset_email',
  LEGACY_EMAIL: 'email',
  LEGACY_PASSWORD: 'password',
  SESSION: 'inmeet_auth_session',
  USERS_DB: 'inmeet_mock_users',
  REMEMBER_ME: 'inmeet_remember_me',
  REQUESTED_PATH: 'inmeet_requested_path',
} as const

// Mock-only: fixed OTP accepted by verify/reset flows when no backend is wired.
export const MOCK_OTP_CODE = '123456'

// Default sign-in is sticky enough to survive a tab close + reopen. Users who
// want a longer-lived session tick "remember me" to push the expiry out to 30
// days. The TTL is the *only* difference between the two flows now — both
// values are persisted to localStorage and gated on `session.expiresAt`.
export const SESSION_DURATION_MS = 24 * 60 * 60 * 1000 // 24 hours
export const REMEMBER_ME_DURATION_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export const AUTH_ERROR_CODES = {
  EMAIL_NOT_FOUND: 'EMAIL_NOT_FOUND',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_EXISTS: 'EMAIL_EXISTS',
  INVALID_OTP: 'INVALID_OTP',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  // The server rejected a field. Distinct from WEAK_PASSWORD, which is the
  // client's own verdict before anything is sent.
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES]

export const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  [AUTH_ERROR_CODES.EMAIL_NOT_FOUND]: 'Invalid email address.',
  [AUTH_ERROR_CODES.INVALID_PASSWORD]: 'Invalid password.',
  [AUTH_ERROR_CODES.INVALID_CREDENTIALS]: 'Invalid email or password.',
  [AUTH_ERROR_CODES.EMAIL_EXISTS]: 'An account with this email already exists.',
  [AUTH_ERROR_CODES.INVALID_OTP]: 'Invalid verification code.',
  [AUTH_ERROR_CODES.WEAK_PASSWORD]: "Password doesn't meet all requirements.",
  [AUTH_ERROR_CODES.VALIDATION_ERROR]: 'Please check the details you entered.',
  [AUTH_ERROR_CODES.UNKNOWN]: 'Something went wrong. Please try again.',
}
